import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { usePageTransition } from './TransitionProvider';
import { useLocale } from '../i18n/LocaleContext';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import logoFinaal from '../images/logo-finaal.webp';
import '../styles/navbar.css';

gsap.registerPlugin(SplitText);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { start } = usePageTransition();
  const { lang, t, loc, switchPath } = useLocale();
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const logoSvgRef = useRef(null);
  const menuHeaderRef = useRef(null);
  const menuOverlayRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const [time, setTime] = React.useState('00:00:00');
  // The animation code drives the ref (it runs outside React); `isOpen` mirrors
  // it so aria-expanded, the scroll lock and the Escape handler stay in sync.
  const [isOpen, setIsOpen] = React.useState(false);
  const isOpenRef = useRef(false);
  const reducedMotion = usePrefersReducedMotion();
  const lastScrollYRef = useRef(window.scrollY);
  const isMenuVisibleRef = useRef(true);
  const isAnimatingRef = useRef(false);
  const splitTextsRef = useRef([]);
  const footerSplitTextsRef = useRef([]);
  // The scroll handler is registered once but has to reach closeMenu, which is
  // declared further down; a ref keeps the listener stable without a circular
  // dependency between the two.
  const closeMenuRef = useRef(null);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const menu = menuRef.current;

    if (!menu) return;

    // Als menu open is en we scrollen naar beneden, sluit menu
    if (currentScrollY > lastScrollYRef.current && currentScrollY > 100) {
      if (isOpenRef.current) {
        closeMenuRef.current?.();
      }
      // Animate menu naar boven (verbergen) - alleen als menu niet al verborgen is
      if (isMenuVisibleRef.current) {
        gsap.killTweensOf(menu);
        gsap.to(menu, {
          duration: 0.4,
          y: -200,
          ease: 'power2.out',
          overwrite: true,
        });
        isMenuVisibleRef.current = false;
      }
    } else if (currentScrollY < lastScrollYRef.current) {
      // Animate menu terug (tonen) - alleen als menu verborgen is
      if (!isMenuVisibleRef.current) {
        gsap.killTweensOf(menu);
        gsap.to(menu, {
          duration: 0.4,
          y: 0,
          ease: 'power2.out',
          overwrite: true,
        });
        isMenuVisibleRef.current = true;
      }
    }

    lastScrollYRef.current = currentScrollY;
  }, []);

  useEffect(() => {
    // Initialize menu position
    const menu = menuRef.current;
    if (menu) {
      gsap.set(menu, { y: 0 });
    }

    // Wait for fonts, then init menu SplitText (bundled plugin — no CDN).
    window.addEventListener('scroll', handleScroll, { passive: true });
    updateTime();
    const timeInterval = setInterval(updateTime, 1000);

    return () => {
      clearInterval(timeInterval);
      window.removeEventListener('scroll', handleScroll);
      // Cleanup GSAP tweens
      if (menu) {
        gsap.killTweensOf(menu);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleScroll]);

  useEffect(() => {
    let cancelled = false;
    const bootNavbar = () => {
      if (cancelled) return;
      splitTextsRef.current.forEach((split) => split.revert?.());
      splitTextsRef.current = [];
      footerSplitTextsRef.current.forEach((split) => split.revert?.());
      footerSplitTextsRef.current = [];
      initNavbar();
    };
    const run = () => setTimeout(bootNavbar, 80);
    if (document.fonts?.ready) {
      document.fonts.ready.then(run);
    } else {
      run();
    }
    return () => {
      cancelled = true;
    };
    // initNavbar reads current menu labels after language switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    const revertSplits = () => {
      splitTextsRef.current.forEach((s) => {
        try { s.revert(); } catch (_) { /* already reverted */ }
      });
      splitTextsRef.current = [];
      footerSplitTextsRef.current.forEach((s) => {
        try { s.revert(); } catch (_) { /* already reverted */ }
      });
      footerSplitTextsRef.current = [];
    };
    const boot = () => {
      if (cancelled) return;
      revertSplits();
      initNavbar();
    };
    const run = () => setTimeout(boot, 80);
    if (document.fonts?.ready) {
      document.fonts.ready.then(run);
    } else {
      run();
    }
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const updateTime = () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', {
      hour12: false,
    });
    setTime(`${timeString} LOCAL`);
  };


  const scrambleText = (elements, duration = 0.4) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()';

    elements.forEach((char) => {
      const originalText = char.textContent;
      let iterations = 0;
      const maxIterations = Math.floor(Math.random() * 6) + 3;

      gsap.set(char, { opacity: 1 });

      const scrambleInterval = setInterval(() => {
        char.textContent = chars[Math.floor(Math.random() * chars.length)];
        iterations++;

        if (iterations >= maxIterations) {
          clearInterval(scrambleInterval);
          char.textContent = originalText;
        }
      }, 25);

      setTimeout(() => {
        clearInterval(scrambleInterval);
        char.textContent = originalText;
      }, duration * 1000);
    });
  };

  const initNavbar = () => {
    const menuOverlay = menuOverlayRef.current;
    const menuItems = document.querySelectorAll('.menu-nav li');

    if (!menuOverlay || !menuItems.length) {
      console.error('Menu elements not found!');
      return;
    }

    gsap.set(menuOverlay, {
      scaleY: 0,
      transformOrigin: 'top center',
    });

    menuItems.forEach((item) => {
      const link = item.querySelector('a');
      if (link) {
        try {
          if (SplitText) {
            const split = new SplitText(link, {
              type: 'words',
              mask: 'words',
              aria: 'none',
            });
            splitTextsRef.current.push(split);

            gsap.set(split.words, {
              yPercent: 120,
            });
          } else {
            gsap.set(link, { opacity: 1 });
          }
        } catch (error) {
          console.error('SplitText error:', error);
          gsap.set(link, { opacity: 1 });
        }
      }
    });

    const footerElements = document.querySelectorAll(
      '.menu-social a, .menu-social span, .menu-time'
    );
    footerElements.forEach((element) => {
      try {
        if (SplitText) {
          const split = new SplitText(element, {
            type: 'chars',
            aria: 'none',
          });
          footerSplitTextsRef.current.push(split);

          gsap.set(split.chars, {
            opacity: 0,
          });
        }
      } catch (error) {
        console.error('SplitText footer error:', error);
      }
    });

    gsap.set(menuItems, {
      opacity: 1,
    });

    const menuFooter = document.querySelector('.menu-footer');
    if (menuFooter) {
      gsap.set(menuFooter, {
        opacity: 1,
        y: 20,
      });
    }
  };

  const toggleMenu = () => {
    if (isAnimatingRef.current) return;

    if (isOpenRef.current) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const openMenu = () => {
    isOpenRef.current = true;
    setIsOpen(true);
    isAnimatingRef.current = true;
    const hamburger = hamburgerRef.current;
    const logo = logoSvgRef.current;

    if (hamburger) hamburger.classList.add('open');
    if (logo) logo.classList.add('rotated');

    const menuOverlay = menuOverlayRef.current;
    const menuItems = document.querySelectorAll('.menu-nav li');
    const menuFooter = document.querySelector('.menu-footer');

    if (reducedMotion) {
      // Snap to the open state: same result, no staggered reveal.
      const words = splitTextsRef.current.flatMap((split) => split.words || []);
      gsap.set(menuOverlay, { scaleY: 1 });
      gsap.set(words.length ? words : menuItems, words.length ? { yPercent: 0 } : { opacity: 1 });
      gsap.set(menuFooter, { y: 0, opacity: 1 });
      gsap.set(footerSplitTextsRef.current.flatMap((s) => s.chars || []), { opacity: 1 });
      gsap.set(document.querySelectorAll('.menu-time'), { opacity: 1 });
      isAnimatingRef.current = false;
      return;
    }

    const tl = gsap.timeline({
      onComplete: () => {
        isAnimatingRef.current = false;
      },
    });

    tl.to(menuOverlay, {
      duration: 0.5,
      scaleY: 1,
      ease: 'power3.out',
    });

    const allWords = splitTextsRef.current.reduce((acc, split) => {
      if (split.words) {
        return acc.concat(split.words);
      }
      return acc;
    }, []);

    if (allWords.length > 0) {
      tl.to(
        allWords,
        {
          duration: 0.75,
          yPercent: 0,
          stagger: 0.05,
          ease: 'power4.out',
        },
        '-=0.3'
      );
    } else {
      tl.to(
        menuItems,
        {
          duration: 0.5,
          opacity: 1,
          stagger: 0.1,
          ease: 'power2.out',
        },
        '-=0.3'
      );
    }

    tl.to(
      menuFooter,
      {
        duration: 0.3,
        y: 0,
        ease: 'power2.out',
        onComplete: () => {
          const timeElement = document.querySelector('.menu-time');
          if (timeElement) {
            gsap.set(timeElement, { opacity: 1 });
          }

          const allFooterChars = footerSplitTextsRef.current.reduce((acc, split) => {
            if (split.chars) {
              return acc.concat(split.chars);
            }
            return acc;
          }, []);

          if (allFooterChars.length > 0) {
            allFooterChars.forEach((char, index) => {
              setTimeout(() => {
                scrambleText([char], 0.4);
              }, index * 30);
            });
          }
        },
      },
      '-=1'
    );
  };

  const closeMenu = () => {
    isOpenRef.current = false;
    setIsOpen(false);
    isAnimatingRef.current = true;
    const hamburger = hamburgerRef.current;
    const logo = logoSvgRef.current;

    if (hamburger) hamburger.classList.remove('open');
    if (logo) logo.classList.remove('rotated');

    const menuOverlay = menuOverlayRef.current;
    const menuItems = document.querySelectorAll('.menu-nav li');
    const menuFooter = document.querySelector('.menu-footer');

    if (reducedMotion) {
      const words = splitTextsRef.current.flatMap((split) => split.words || []);
      gsap.set(menuOverlay, { scaleY: 0 });
      gsap.set(words.length ? words : menuItems, words.length ? { yPercent: 120 } : { opacity: 0 });
      gsap.set(menuFooter, { y: 20 });
      gsap.set(footerSplitTextsRef.current.flatMap((s) => s.chars || []), { opacity: 0 });
      gsap.set(document.querySelectorAll('.menu-time'), { opacity: 0 });
      isAnimatingRef.current = false;
      return;
    }

    const tl = gsap.timeline({
      onComplete: () => {
        isAnimatingRef.current = false;
      },
    });

    const allWords = splitTextsRef.current.reduce((acc, split) => {
      if (split.words) {
        return acc.concat(split.words);
      }
      return acc;
    }, []);

    tl.to(
      [menuFooter],
      {
        duration: 0.3,
        y: 20,
        ease: 'power2.in',
        onStart: () => {
          const timeElement = document.querySelector('.menu-time');
          if (timeElement) {
            gsap.set(timeElement, { opacity: 0 });
          }
          const allFooterChars = footerSplitTextsRef.current.reduce((acc, split) => {
            if (split.chars) {
              return acc.concat(split.chars);
            }
            return acc;
          }, []);
          gsap.set(allFooterChars, { opacity: 0 });
        },
      }
    );

    if (allWords.length > 0) {
      tl.to(
        allWords,
        {
          duration: 0.25,
          yPercent: 120,
          stagger: -0.025,
          ease: 'power2.in',
        },
        '-=0.25'
      );
    } else {
      tl.to(
        menuItems,
        {
          duration: 0.25,
          opacity: 0,
          stagger: -0.025,
          ease: 'power2.in',
        },
        '-=0.25'
      );
    }

    tl.to(
      menuOverlay,
      {
        duration: 0.5,
        scaleY: 0,
        ease: 'power3.inOut',
      },
      '-=0.2'
    );
  };

  closeMenuRef.current = closeMenu;

  // Escape closes the overlay and hands focus back to the button that opened it.
  // Opening moves focus into the overlay so keyboard users land on the links
  // instead of continuing past them.
  useEffect(() => {
    if (!isOpen) return undefined;

    menuOverlayRef.current?.querySelector('a')?.focus();

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      closeMenu();
      toggleButtonRef.current?.focus();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Lock the page behind the overlay — including Lenis, which ignores
  // `overflow: hidden` because it scrolls with transforms.
  useEffect(() => {
    if (!isOpen) return undefined;

    const lenis = typeof window !== 'undefined' ? window.__lenis : null;
    const previousOverflow = document.body.style.overflow;
    lenis?.stop?.();
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      lenis?.start?.();
    };
  }, [isOpen]);

  // A route change from anywhere (browser back, a link outside the menu) should
  // not leave the overlay hanging open.
  useEffect(() => {
    if (isOpenRef.current) closeMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLinkClick = (path, e) => {
    e.preventDefault();
    const target = loc(path);

    // Only navigate if not already on the target page
    if (location.pathname !== target) {
      if (isOpenRef.current) {
        // Close menu first, then navigate after closing animation
        closeMenu();
        setTimeout(() => {
          start(target);
        }, 600); // Wait for menu close animation
      } else {
        // Navigate immediately if menu is not open
        start(target);
      }
    } else if (isOpenRef.current) {
      // If already on page, just close the menu
      closeMenu();
    }
  };

  const handleLangSwitch = (nextLang, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (nextLang === lang) return;
    if (isOpenRef.current) closeMenu();
    navigate(switchPath(nextLang));
  };

  return (
    <nav className="menu" ref={menuRef} id="navbar">
      <div className="menu-header" ref={menuHeaderRef}>
        <a href={loc('/')} className="menu-logo" onClick={(e) => handleLinkClick('/', e)}>
          <span ref={logoSvgRef} className="menu-logo-text">
            <img src={logoFinaal} alt="SK — Sander Kuyken" className="menu-logo-img" />
          </span>
        </a>
        <div className="menu-header-end">
          <div className="menu-lang">
            <a
              href={switchPath('en')}
              className={lang === 'en' ? 'is-active' : undefined}
              aria-current={lang === 'en' ? 'true' : undefined}
              onClick={(e) => handleLangSwitch('en', e)}
            >
              EN
            </a>
            <span className="menu-lang-sep" aria-hidden="true">/</span>
            <a
              href={switchPath('nl')}
              className={lang === 'nl' ? 'is-active' : undefined}
              aria-current={lang === 'nl' ? 'true' : undefined}
              onClick={(e) => handleLangSwitch('nl', e)}
            >
              NL
            </a>
          </div>
          <button
            type="button"
            className="menu-toggle"
            ref={toggleButtonRef}
            aria-label={t('nav.toggle')}
            aria-expanded={isOpen}
            aria-controls="menu-overlay"
            onClick={toggleMenu}
          >
            <div className="menu-hamburger-icon" ref={hamburgerRef}>
              <span className="menu-item"></span>
              <span className="menu-item"></span>
            </div>
          </button>
        </div>
      </div>
      <div className="menu-overlay" id="menu-overlay" ref={menuOverlayRef} inert={!isOpen ? '' : undefined}>
        <nav className="menu-nav">
          <ul>
            <li>
              <a href={loc('/')} onClick={(e) => handleLinkClick('/', e)}>
                {t('nav.home')}
              </a>
            </li>
            <li>
              <a href={loc('/about')} onClick={(e) => handleLinkClick('/about', e)}>
                {t('nav.about')}
              </a>
            </li>
            <li>
              <a href={loc('/works')} onClick={(e) => handleLinkClick('/works', e)}>
                {t('nav.works')}
              </a>
            </li>
            <li>
              <a href={loc('/services')} onClick={(e) => handleLinkClick('/services', e)}>
                {t('nav.services')}
              </a>
            </li>
            <li>
              <a href={loc('/insights')} onClick={(e) => handleLinkClick('/insights', e)}>
                {t('nav.insights')}
              </a>
            </li>
            <li>
              <a href={loc('/contact')} onClick={(e) => handleLinkClick('/contact', e)}>
                {t('nav.contact')}
              </a>
            </li>
          </ul>
        </nav>
        <div className="menu-footer">
          <div className="menu-social">
            <a href="https://www.artstation.com/kuyken" target="_blank" rel="noopener noreferrer">
              <span>&#9654;</span> ArtStation
            </a>
            <a
              href="https://www.linkedin.com/in/sander-kuyken-8a384716b/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>&#9654;</span> LinkedIn
            </a>
          </div>
          <div className="menu-time">{time}</div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

